import { useTextTranslation } from "../../utils/useTextTranslation";
import React, { useContext, useEffect, useRef, useState } from "react";
import SinglePlayIcon from "../../assets/icons/svg/SinglePlayIcon";
import localizationStrings from "../../statics/localization";
import { fancyTimeFormat } from "../../utils/fancyTimeFormat";
import { SessionContext } from "../../context/SessionContext";
import { useHistory, useLocation } from "react-router-dom";
import LikeAction from "../likeAction";
import FavoriteAction from "../favoriteAction";
import RtukAgeIcon from "../../assets/icons/svg/RtukAgeIcon";
import RtukGeneralIcon from "../../assets/icons/svg/RtukGeneralIcon";
import RtukSevenAgeIcon from "../../assets/icons/svg/RtukSevenAgeIcon";
import RtukThirteenAgeIcon from "../../assets/icons/svg/RtukThirteenAgeIcon";
import getParentalAdvisoryElements from "../../utils/getParentalAdvisory";
import useOutsideEnter from "../../utils/useOutsideEnter";
import TrailerPlayer from "../trailerPlayer";
import TrailerAudioOnIcon from "../../assets/icons/svg/TrailerAudioOnIcon";
import AudioOffIcon from "../../assets/icons/svg/AudioOffIcon";
import {
  setTrailerAudioOnGlobal,
  getTrailerAudioOnGlobal
} from "../../statics/config";
import mainLoadingGif from "../../assets/loading.gif";
import { prettyTimeFormatter } from "../../utils/prettyTimeFormatter";
import { getPosterUrlByType } from "../../utils/findPoster";

const MiniCatchupDetail = ({
  closeModal,
  catchupData,
  isFirstRow,
  isRight,
  isLeft
}) => {
  const translateText = useTextTranslation();
  const [hasScale, setHasScale] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [width, setWidth] = useState(catchupData?.position?.width);
  const [showLoading, setShowLoading] = useState(false);

  const { path, setShowLoginMessage, hasLoggedIn } = useContext(SessionContext);
  const history = useHistory();
  const location = useLocation();
  const positionLeft = useRef(
    catchupData?.position?.x - (0.2 * catchupData?.position?.width) / 2
  );
  const positionTop = useRef(null);
  const leaveTimeoutRef = useRef();
  const timeoutRef = useRef(null);
  const modalRef = useRef(null);
  const loadingTimeoutId = useRef(null);

  // Get the catchup item (VodItem passes it as 'vod' property)
  const catchupItem = catchupData?.vod;

  useOutsideEnter(modalRef, () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
    }
    setHasScale(false);
    // Add a small delay to allow the scale-down animation to complete
    setTimeout(() => {
      setIsVisible(false);
      closeModal();
    }, 200);
  });

  const calcDuration = () => {
    if (catchupItem?.type === "CATCHUPMOVIE") {
      return prettyTimeFormatter(catchupItem?.duration);
    } else if (catchupItem?.type === "CATCHUPSERIES") {
      return localizationStrings.series;
    }
  };

  const onClickCatchup = (isPlay = false) => {
    let item = catchupItem;
    if (item?.type === "CATCHUPMOVIE") {
      history.push(`/dashboard/catchupdetail/${item?.id}${isPlay ? "?play=1" : ""}`);
    } else if (item?.type === "CATCHUPSERIES") {
      history.push(`/dashboard/catchupdetail/${item?.id}${isPlay ? "?play=1" : ""}`);
    }

    loadingTimeoutId.current && clearTimeout(loadingTimeoutId.current);
    loadingTimeoutId.current = setTimeout(() => {
      setShowLoading(false);
      closeModal();
    }, 2000);
  };

  const playContent = async () => {
    onClickCatchup(true);
  };

  useEffect(() => {
    if (catchupItem) {
      let modalWidth;
      
      // Calculate modal width responsively
      modalWidth = Number(((window.innerWidth / 1920) * 728).toFixed(1));

      // Calculate position with better centering
      positionLeft.current = catchupData?.position.x - (modalWidth - catchupData.position.width) / 2;
      positionTop.current = window.pageYOffset + catchupData?.position.y - catchupData?.position.height / 2;

      if (isFirstRow) {
        positionTop.current = catchupData?.position.y;
      }

      // Keep modal within viewport boundaries
      if (isLeft) {
        positionLeft.current = 0;
      }
      
      if (positionLeft.current + modalWidth > window.innerWidth) {
        positionLeft.current = window.innerWidth - modalWidth - 20; // Add some padding
      }

      if (positionLeft.current < 0) {
        positionLeft.current = 20; // Add some padding
      }

      timeoutRef.current && clearTimeout(timeoutRef.current);
      
      // First make visible, then animate scale
      setIsVisible(true);
      setWidth(modalWidth);
      
      // Small delay to ensure DOM is ready before scaling
      setTimeout(() => {
        setHasScale(true);
      }, 10);
    }
  }, [catchupData]);

  if (!catchupItem) return null;

  return (
    <div
      className={`modal-hover ${hasScale ? "scale" : ""} ${isVisible ? "visible" : ""}`}
      style={{
        top: positionTop.current + "px",
        width: width + "px",
        left: positionLeft.current + "px",
        transformOrigin: `${((catchupData?.position?.x + catchupData?.position?.width / 2) - positionLeft.current) / width * 100}% top`
      }}
      ref={modalRef}
    >
      <div className={`modal-preview-vod`}>
        <div className="mini-vod-detail-container">
          <div className="image-wrapper">
            <img
              className={`vod-preview-img ${hasScale ? "scale" : ""}`}
              src={getPosterUrlByType(catchupItem?.posters, "Web Landscape")}
              alt={translateText(catchupItem?.title)}
            />
          </div>
          <div className={`preview-info ${hasScale ? "scale" : ""}`}>
            <div className="mini-voddetail-row">
              <div className="vod-title">
                <span>{translateText(catchupItem?.title)}</span>
              </div>
              <div className="button-group">
                <FavoriteAction vod={catchupItem} newVodDetail />
                <LikeAction vod={catchupItem} newVodDetail />
                <div
                  className="action-button primary"
                  onClick={() => {
                    if (!showLoading) {
                      if (hasLoggedIn) {
                        setShowLoading(true);
                        playContent();
                      } else {
                        setShowLoginMessage(true);
                      }
                    }
                  }}
                  style={{
                    cursor: "pointer"
                  }}
                >
                  <span className="icon">
                    {showLoading ? (
                      <div className="loading-gif">
                        <img src={mainLoadingGif} />
                      </div>
                    ) : (
                      <SinglePlayIcon />
                    )}
                  </span>
                  <span className={`action-label`}>
                    {localizationStrings.play}
                  </span>
                </div>
              </div>
            </div>
            <div className="mini-voddetail-row">
              <div
                className="vod-info-wrapper"
                style={{ justifyContent: "flex-start" }}
              >
                <div className="content-indicators">
                  <div className="rating-group">
                    {catchupItem?.maturityRating?.age === "G" && (
                      <div className="rating">
                        <RtukGeneralIcon />
                      </div>
                    )}
                    {catchupItem?.maturityRating?.age === "7+" && (
                      <div className="rating">
                        <RtukSevenAgeIcon />
                      </div>
                    )}
                    {catchupItem?.maturityRating?.age === "13+" && (
                      <div className="rating">
                        <RtukThirteenAgeIcon />
                      </div>
                    )}
                    {catchupItem?.maturityRating?.age === "18+" && (
                      <div className="rating">
                        <RtukAgeIcon />
                      </div>
                    )}
                  </div>
                  {Array.isArray(catchupItem?.maturityRating?.categories) &&
                  catchupItem?.maturityRating?.categories.length > 0 ? (
                    <div className="parental-advisory">
                      {getParentalAdvisoryElements(
                        catchupItem?.maturityRating?.categories
                      )}
                    </div>
                  ) : null}
                </div>

                <div className="vod-info">
                  {catchupItem?.type === "CATCHUPMOVIE"
                    ? localizationStrings.movie
                    : localizationStrings.series}
                </div>
                <div>&nbsp;</div>
                <div>&nbsp;</div>
                <div className="vod-info">{catchupItem?.year}</div>
                <div>&nbsp;</div>
                <div>&nbsp;</div>
                <div className="vod-info">{calcDuration()}</div>
              </div>
            </div>
            <div className="mini-voddetail-row">
              <div className="description-title">
                {localizationStrings.storyline}
              </div>
            </div>
            <div className="mini-voddetail-row" style={{ marginTop: ".52vw" }}>
              <div className="description" title={translateText(catchupItem?.summary)}>
                {translateText(catchupItem?.description || catchupItem?.summary)}
              </div>
            </div>

            <div
              className="mini-voddetail-row"
              style={{ marginBottom: "29px" }}
            >
              <div className="inspect-button" onClick={() => onClickCatchup()}>
                {localizationStrings.inspect}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiniCatchupDetail;